/**
 * Wild Sprites — visible wandering wild Surggies in encounter zones.
 *
 * Not random-step encounters. These are physical entities the player can
 * see on the map and walk into. Engine-pure: no DOM/React, SSR-safe.
 *
 * The manager:
 *   - Spawns wilds inside per-zone bounds, throttled by a spawn interval and
 *     a maxConcurrent cap.
 *   - Drives a per-sprite wander state machine (idle / walking / paused).
 *   - Despawns sprites whose ttlMs elapses with no interaction.
 *   - Reports collisions via consumeEncounter, which removes the sprite so a
 *     battle isn't triggered twice.
 */

export type WildSpriteFacing = "down" | "left" | "right" | "up";
export type WildSpriteWanderState = "idle" | "walking" | "paused";

export type WildSprite = {
  id: string;
  speciesId: string;
  level: number;
  x: number;
  y: number;
  zoneId: string;
  facing: WildSpriteFacing;
  animFrame: number;
  wanderState: WildSpriteWanderState;
  wanderTarget?: { x: number; y: number };
  ttlMs: number;
};

export type ZoneSpawnEntry = {
  speciesId: string;
  weight: number;
  levelMin: number;
  levelMax: number;
};

/**
 * Per-zone spawn configuration. Bounds are optional here because the public
 * spec doesn't include them, but a manager that can't bound spawns is
 * useless — so callers SHOULD pass bounds. When omitted, spawns are skipped.
 */
export type ZoneSpawnConfig = {
  zoneId: string;
  maxConcurrent: number;
  spawnIntervalMs: number;
  speciesPool: ZoneSpawnEntry[];
  /** Inclusive tile bounds for this zone. If omitted, no spawns occur. */
  bounds?: { minX: number; minY: number; maxX: number; maxY: number };
};

type ZoneRuntime = {
  config: ZoneSpawnConfig;
  /** Time since last spawn attempt, ms. */
  spawnTimerMs: number;
};

type SpriteRuntime = {
  /** Time remaining until next wander decision, ms. */
  wanderTimerMs: number;
  /** Animation frame cycler, ms. */
  animTimerMs: number;
};

const TTL_DEFAULT_MS = 60_000;
const ANIM_FRAME_INTERVAL_MS = 200;
const ANIM_FRAME_COUNT = 4;
const WANDER_TICK_MIN_MS = 600;
const WANDER_TICK_MAX_MS = 1400;
const PAUSE_MIN_MS = 800;
const PAUSE_MAX_MS = 2000;
const MIN_SPAWN_DIST_FROM_PLAYER = 4;
const MAX_SPAWN_ATTEMPTS = 12;

export class WildSpritesManager {
  private readonly zones: Map<string, ZoneRuntime> = new Map();
  private readonly active: WildSprite[] = [];
  private readonly runtime: Map<string, SpriteRuntime> = new Map();
  private nextId: number = 1;

  constructor(spawnConfigs: ZoneSpawnConfig[]) {
    for (const cfg of spawnConfigs) {
      this.zones.set(cfg.zoneId, { config: cfg, spawnTimerMs: 0 });
    }
  }

  get sprites(): readonly WildSprite[] {
    return this.active;
  }

  update(
    dtMs: number,
    playerTile: { x: number; y: number; zoneId: string },
    occupied: (x: number, y: number) => boolean,
  ): void {
    // 1. Tick TTL + animation + wander timers for every active sprite.
    for (let i = this.active.length - 1; i >= 0; i--) {
      const sprite = this.active[i];
      sprite.ttlMs -= dtMs;
      if (sprite.ttlMs <= 0) {
        this.removeAt(i);
        continue;
      }

      const rt = this.runtime.get(sprite.id);
      if (!rt) continue;

      // Animation frame cycles whenever the sprite is walking; idle/paused
      // pin to frame 0 so the sprite looks at rest.
      if (sprite.wanderState === "walking") {
        rt.animTimerMs += dtMs;
        while (rt.animTimerMs >= ANIM_FRAME_INTERVAL_MS) {
          rt.animTimerMs -= ANIM_FRAME_INTERVAL_MS;
          sprite.animFrame = (sprite.animFrame + 1) % ANIM_FRAME_COUNT;
        }
      } else {
        sprite.animFrame = 0;
        rt.animTimerMs = 0;
      }

      rt.wanderTimerMs -= dtMs;
      if (rt.wanderTimerMs <= 0) {
        this.stepWander(sprite, rt, occupied);
      }
    }

    // 2. Per-zone spawn attempts.
    for (const zoneRt of this.zones.values()) {
      zoneRt.spawnTimerMs += dtMs;
      const cfg = zoneRt.config;
      if (zoneRt.spawnTimerMs < cfg.spawnIntervalMs) continue;
      zoneRt.spawnTimerMs = 0;

      const inZone = this.countInZone(cfg.zoneId);
      if (inZone >= cfg.maxConcurrent) continue;

      // Only respect player-distance gating when the player is in this zone.
      const respectPlayerDist = playerTile.zoneId === cfg.zoneId;
      this.trySpawn(cfg, respectPlayerDist ? playerTile : null, occupied);
    }
  }

  consumeEncounter(playerTile: { x: number; y: number }): WildSprite | null {
    for (let i = 0; i < this.active.length; i++) {
      const s = this.active[i];
      if (
        Math.abs(s.x - playerTile.x) <= 1 &&
        Math.abs(s.y - playerTile.y) <= 1
      ) {
        const sprite = s;
        this.removeAt(i);
        return sprite;
      }
    }
    return null;
  }

  removeSprite(id: string): void {
    const i = this.active.findIndex((s) => s.id === id);
    if (i >= 0) this.removeAt(i);
  }

  clear(): void {
    this.active.length = 0;
    this.runtime.clear();
    for (const zoneRt of this.zones.values()) {
      zoneRt.spawnTimerMs = 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private removeAt(index: number): void {
    const sprite = this.active[index];
    this.runtime.delete(sprite.id);
    this.active.splice(index, 1);
  }

  private countInZone(zoneId: string): number {
    let n = 0;
    for (const s of this.active) {
      if (s.zoneId === zoneId) n++;
    }
    return n;
  }

  private trySpawn(
    cfg: ZoneSpawnConfig,
    playerTile: { x: number; y: number } | null,
    occupied: (x: number, y: number) => boolean,
  ): void {
    if (!cfg.bounds) return;
    if (cfg.speciesPool.length === 0) return;

    const { minX, minY, maxX, maxY } = cfg.bounds;
    if (maxX < minX || maxY < minY) return;

    for (let attempt = 0; attempt < MAX_SPAWN_ATTEMPTS; attempt++) {
      const x = minX + Math.floor(Math.random() * (maxX - minX + 1));
      const y = minY + Math.floor(Math.random() * (maxY - minY + 1));

      if (occupied(x, y)) continue;
      if (this.tileHasSprite(x, y)) continue;
      if (
        playerTile &&
        Math.abs(x - playerTile.x) < MIN_SPAWN_DIST_FROM_PLAYER &&
        Math.abs(y - playerTile.y) < MIN_SPAWN_DIST_FROM_PLAYER
      ) {
        continue;
      }

      const entry = pickWeighted(cfg.speciesPool);
      if (!entry) return;
      const level = randIntInclusive(entry.levelMin, entry.levelMax);

      const sprite: WildSprite = {
        id: `wild_${cfg.zoneId}_${this.nextId++}`,
        speciesId: entry.speciesId,
        level,
        x,
        y,
        zoneId: cfg.zoneId,
        facing: randomFacing(),
        animFrame: 0,
        wanderState: "idle",
        ttlMs: TTL_DEFAULT_MS,
      };
      this.active.push(sprite);
      this.runtime.set(sprite.id, {
        wanderTimerMs: randIntInclusive(WANDER_TICK_MIN_MS, WANDER_TICK_MAX_MS),
        animTimerMs: 0,
      });
      return;
    }
  }

  private tileHasSprite(x: number, y: number): boolean {
    for (const s of this.active) {
      if (s.x === x && s.y === y) return true;
    }
    return false;
  }

  private stepWander(
    sprite: WildSprite,
    rt: SpriteRuntime,
    occupied: (x: number, y: number) => boolean,
  ): void {
    // 60% walk, 40% pause.
    if (Math.random() < 0.6) {
      const dir = randomCardinal();
      const nx = sprite.x + dir.dx;
      const ny = sprite.y + dir.dy;
      sprite.facing = dir.facing;

      if (!occupied(nx, ny) && !this.tileHasSprite(nx, ny)) {
        sprite.x = nx;
        sprite.y = ny;
        sprite.wanderState = "walking";
        sprite.wanderTarget = { x: nx, y: ny };
      } else {
        // Blocked — fall through to idle so the sprite doesn't tunnel.
        sprite.wanderState = "idle";
        sprite.wanderTarget = undefined;
      }
      rt.wanderTimerMs = randIntInclusive(WANDER_TICK_MIN_MS, WANDER_TICK_MAX_MS);
    } else {
      sprite.wanderState = "paused";
      sprite.wanderTarget = undefined;
      rt.wanderTimerMs = randIntInclusive(PAUSE_MIN_MS, PAUSE_MAX_MS);
    }
  }
}

// -----------------------------------------------------------------------------
// helpers (module-private)
// -----------------------------------------------------------------------------

function randIntInclusive(min: number, max: number): number {
  if (max < min) return min;
  return min + Math.floor(Math.random() * (max - min + 1));
}

function randomFacing(): WildSpriteFacing {
  const opts: WildSpriteFacing[] = ["down", "left", "right", "up"];
  return opts[Math.floor(Math.random() * opts.length)];
}

function randomCardinal(): {
  dx: number;
  dy: number;
  facing: WildSpriteFacing;
} {
  const r = Math.floor(Math.random() * 4);
  if (r === 0) return { dx: 0, dy: 1, facing: "down" };
  if (r === 1) return { dx: -1, dy: 0, facing: "left" };
  if (r === 2) return { dx: 1, dy: 0, facing: "right" };
  return { dx: 0, dy: -1, facing: "up" };
}

function pickWeighted(pool: ZoneSpawnEntry[]): ZoneSpawnEntry | null {
  let total = 0;
  for (const entry of pool) {
    if (entry.weight > 0) total += entry.weight;
  }
  if (total <= 0) return null;
  let roll = Math.random() * total;
  for (const entry of pool) {
    if (entry.weight <= 0) continue;
    roll -= entry.weight;
    if (roll <= 0) return entry;
  }
  return pool[pool.length - 1] ?? null;
}
